import { useEffect, useState } from 'react';
import { ProxiedSignInPage, UserIdentity } from '@backstage/core-components';
import {
  googleAuthApiRef,
  useApi,
  type IdentityApi,
} from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import { Progress } from '../fallbacks/Fallbacks';
import './SignInScreen.css';

/**
 * The key and the ids are the ones `SignInPage` of core-components uses, so a
 * session started under the stock page is picked up here and the other way
 * round.
 */
const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';
const GOOGLE_ID = 'google-auth-provider';
const GUEST_ID = 'guest';

function readProvider(): string | null {
  try {
    return localStorage.getItem(PROVIDER_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeProvider(id: string | null) {
  try {
    if (id === null) localStorage.removeItem(PROVIDER_STORAGE_KEY);
    else localStorage.setItem(PROVIDER_STORAGE_KEY, id);
  } catch {
    // Storage blocked: sign-in still works, only the session is not restored.
  }
}

/** Forgets the chosen provider on sign-out, as `SignInPage` does. */
function forgetOnSignOut(identityApi: IdentityApi): IdentityApi {
  return {
    getProfileInfo: () => identityApi.getProfileInfo(),
    getBackstageIdentity: () => identityApi.getBackstageIdentity(),
    getCredentials: () => identityApi.getCredentials(),
    signOut: async () => {
      writeProvider(null);
      await identityApi.signOut();
    },
  };
}

const iconProps = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function Hero() {
  return (
    <section className="atlas-signin__hero">
      <div
        className="atlas-signin__circle"
        style={{ width: 760, height: 760, right: -300, top: -280 }}
      />
      <div
        className="atlas-signin__circle"
        style={{ width: 440, height: 440, left: -160, bottom: -120 }}
      />

      <div className="atlas-signin__brand">
        <div className="atlas-signin__brand-mark">
          <svg {...iconProps} width={22} height={22} stroke="#FFFFFF">
            <path d="M12 2 3 7l9 5 9-5-9-5Z" />
            <path d="m3 12 9 5 9-5" />
            <path d="m3 17 9 5 9-5" />
          </svg>
        </div>
        <span className="atlas-signin__brand-name">Uzum Fintech</span>
      </div>
    </section>
  );
}

/**
 * The sign-in screen itself. It does what `SignInPage` of core-components
 * does — restores the last provider's session on load, remembers the provider
 * on success, forgets it on sign-out — but draws its own layout, which the
 * stock page (a grid of `InfoCard`s) cannot be styled into.
 */
export function SignInScreen(props: SignInPageProps & { allowGuest: boolean }) {
  const { onSignInSuccess, allowGuest } = props;
  const googleAuthApi = useApi(googleAuthApiRef);

  const [restoring, setRestoring] = useState(
    () => readProvider() === GOOGLE_ID,
  );
  const [guest, setGuest] = useState(
    () => allowGuest && readProvider() === GUEST_ID,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(
    () =>
      // The OAuth handler redirects back with `?error=` when the backend refuses
      // the profile, e.g. an address outside the allow-list.
      new URLSearchParams(window.location.search).get('error') ?? undefined,
  );

  const succeed = (id: string, identityApi: IdentityApi) => {
    writeProvider(id);
    onSignInSuccess(forgetOnSignOut(identityApi));
  };

  // A Google session survives a reload as a refresh cookie; pick it up without
  // a popup, exactly like the stock page's loader.
  useEffect(() => {
    if (!restoring) return undefined;
    let cancelled = false;
    (async () => {
      const response = await googleAuthApi.getBackstageIdentity({
        optional: true,
      });
      if (cancelled) return;
      if (!response) {
        setRestoring(false);
        return;
      }
      const profile = await googleAuthApi.getProfile();
      if (cancelled) return;
      succeed(
        GOOGLE_ID,
        UserIdentity.create({
          identity: response.identity,
          profile,
          authApi: googleAuthApi,
        }),
      );
    })().catch(() => {
      if (cancelled) return;
      writeProvider(null);
      setRestoring(false);
    });
    return () => {
      cancelled = true;
    };
    // Runs once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInWithGoogle = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const response = await googleAuthApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!response) {
        throw new Error('провайдер Google не настроен для входа');
      }
      const profile = await googleAuthApi.getProfile();
      succeed(
        GOOGLE_ID,
        UserIdentity.create({
          identity: response.identity,
          profile,
          authApi: googleAuthApi,
        }),
      );
    } catch (e) {
      setError(`Не удалось войти: ${e instanceof Error ? e.message : e}`);
      setBusy(false);
    }
  };

  // Back to the screen with the reason, instead of the stock dead-end panel.
  function GuestFailed({ error: cause }: { error?: Error }) {
    useEffect(() => {
      writeProvider(null);
      setGuest(false);
      setError(`Не удалось войти как гость: ${cause?.message ?? cause}`);
    }, [cause]);
    return null;
  }

  if (restoring) {
    return <Progress />;
  }

  // The guest flow is the backend's proxied one; `ProxiedSignInPage` already
  // signs in on mount and keeps the token refreshed, so it is reused as is.
  if (guest) {
    return (
      <ProxiedSignInPage
        {...props}
        provider={GUEST_ID}
        onSignInSuccess={identityApi => succeed(GUEST_ID, identityApi)}
        ErrorComponent={GuestFailed}
      />
    );
  }

  return (
    <div className="atlas-signin">
      <Hero />

      <main className="atlas-signin__main">
        <div className="atlas-signin__panel">
          <div className="atlas-signin__stack">
            <h2 className="atlas-signin__title">Вход</h2>
            <p className="atlas-signin__lead">
              Войдите через корпоративный аккаунт, чтобы видеть свои сервисы и
              команды.
            </p>
          </div>

          {error && (
            <p className="atlas-signin__error" role="alert">
              {error}
            </p>
          )}

          <div className="atlas-signin__stack atlas-signin__stack--wide">
            <button
              type="button"
              className="atlas-signin__button atlas-signin__button--primary"
              onClick={signInWithGoogle}
              disabled={busy}
            >
              <svg {...iconProps}>
                <rect x="3" y="7" width="18" height="13" rx="2" />
                <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="M3 13h18" />
              </svg>
              Войти через Google Workspace
            </button>
            <p className="atlas-signin__note">
              Только для корпоративных аккаунтов
            </p>
          </div>

          {allowGuest && (
            <div className="atlas-signin__guest">
              <div className="atlas-signin__divider">или</div>
              <div className="atlas-signin__stack">
                <button
                  type="button"
                  className="atlas-signin__button atlas-signin__button--secondary"
                  onClick={() => setGuest(true)}
                  disabled={busy}
                >
                  <svg {...iconProps} width={18} height={18}>
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21a8 8 0 0 1 16 0" />
                  </svg>
                  Продолжить как гость
                </button>
                <p className="atlas-signin__note">
                  Без подтверждённой личности часть функций будет недоступна.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
